<template>
  <BaseCard :error="error">
    <SendMessage
      v-if="showMessageModal"
      :selectedMessageType="customer.email ? 'email' : 'sms'"
      :customerData="{
        customerId: customer.id.toString(),
        customerName: name,
        customerEmail: customer.email,
        customerMobile: phoneNumber,
      }"
      @close="showMessageModal = false"
    />
    <div :class="[$style.base, { [$style.loading]: loading }]">
      <BaseSpinner v-if="loading" />
      <Flex
        direction="column"
        :class="$style.content"
      >
        <Flex
          direction="column"
          :gap=".5"
        >
          <BaseLabel
            v-if="customer.blocked"
            color="error"
            v-test="'blocked-customer-notification'"
          >
            {{ $t('global.blocked') }}
          </BaseLabel>
          <Flex
            justify="between"
            align="center"
            :gap=".5"
          >
            <Flex
              align="center"
              :gap=".5"
            >
              <BaseAvatar :name="name" />
              <BaseText
                bold
                :maxLines="2"
              >
                {{ name }}
              </BaseText>
            </Flex>
            <BaseMore
              v-if="hasActions && actions?.length"
              :options="actions.map(option => option === 'remove' ? { name: 'remove', icon: 'close-circle', label: $t('global.actions.clear') } : option)"
              size="s"
              @select="onOptionSelect"
              v-test="'customer-more'"
            />
          </Flex>
          <div :class="$style.center">
            <BaseText
              v-if="phoneNumber"
              oneLine
              size="s"
              color="secondary"
              iconBefore="phone"
            >
              {{ phoneNumber }}
            </BaseText>
            <BaseText
              v-if="!phoneNumber"
              color="warning"
              size="s"
              iconBefore="phone"
              v-test="'no-phone-notification'"
            >
              {{ $t('customer_details.no_phone') }}
            </BaseText>
            <BaseText
              v-if="customer.email"
              oneLine
              size="s"
              color="secondary"
              iconBefore="email"
            >
              {{ customer.email }}
            </BaseText>
            <BaseText
              v-else
              size="s"
              color="warning"
              iconBefore="email"
              v-test="'no-email-notification'"
            >
              {{ $t('customer_details.no_email') }}
            </BaseText>
            <div v-if="showAddress">
              <BaseText
                v-if="hasFullAddress"
                oneLine
                size="s"
                color="secondary"
                iconBefore="pin"
              >
                {{ customer.address }}, {{ customer.zipcode }}
                {{ customer.city }}
              </BaseText>
              <BaseText
                v-else
                size="s"
                color="warning"
                iconBefore="pin"
                v-test="'no-address-notification'"
              >
                {{ hasIncompleteAddress ? $t('customer_details.address_incomplete') : $t('customer_details.no_address') }}
              </BaseText>
            </div>
            <div v-if="showMedicalData">
              <BaseText
                v-if="customer.dateOfBirth"
                oneLine
                size="s"
                color="secondary"
                iconBefore="gift"
              >
                {{ customer.dateOfBirth }}
              </BaseText>
              <BaseText
                v-else
                size="s"
                color="warning"
                iconBefore="gift"
                v-test="'no-birthday-notification'"
              >
                {{ $t('customer_details.no_date_of_birth') }}
              </BaseText>
              <BaseText
                v-if="customer.personalRegistrationNumber"
                oneLine
                size="s"
                color="secondary"
                iconBefore="person"
              >
                {{ customer.personalRegistrationNumber }}
              </BaseText>
              <BaseText
                v-else
                size="s"
                color="warning"
                iconBefore="person"
                v-test="'no-bsn-notification'"
              >
                {{ $t('customer_details.no_bsn') }}
              </BaseText>
              <BaseText
                v-if="customer.insuranceProvider"
                oneLine
                size="s"
                color="secondary"
                iconBefore="umbrella"
              >
                {{ customer.insuranceProvider }}
              </BaseText>
              <BaseText
                v-else
                size="s"
                color="warning"
                iconBefore="umbrella"
                v-test="'no-insurance-provider-notification'"
              >
                {{ $t('customer_details.no_insurance_provider') }}
              </BaseText>
            </div>
          </div>
        </Flex>
        <Flex
          :gap=".5"
          wrap
          :class="$style.buttons"
          v-if="hasActions"
          v-test="'customer-actions'"
        >
          <BaseButton
            v-if="hasViewCustomer"
            icon="eye"
            color="inverted"
            :tooltip="$t('global.actions.view_customer')"
            :routerLink="{ name: 'customer-overview', params: { customerId: customer.id } }"
            v-test="'customer-view'"
          />
          <BaseButton
            v-if="phoneNumber && device.mobile"
            :href="`tel:${phoneNumber}`"
            icon="phone"
            color="inverted"
            :tooltip="$t('global.actions.call_customer')"
            v-test="'customer-call'"
          />
          <BaseButton
            v-if="customer.email || phoneNumber"
            icon="email"
            color="inverted"
            :tooltip="$t('customers.send_message')"
            @click="showMessageModal = true"
            v-test="'customer-message'"
          />
        </Flex>
        <Flex
          v-if="hasBottomAlerts"
          direction="column"
          :gap=".5"
          :class="$style.bottom"
          v-test="'customer-details-alerts'"
        >
          <BaseAlert
            v-if="totalDue"
            color="warning"
            iconBefore="shopping-cart"
            :text="`${$t('customer_details.money_outstanding', {
            amount: filters.currency(totalDue)
          })}`"
            size="s"
            v-test="'amount-due-notification'"
          />
          <BaseAlert
            v-if="showNewCustomerAlert"
            size="s"
            :text="`🎉 ${$t('customer_details.first_appointment', { name: customer.firstName })}`"
            v-test="'new-customer-notification'"
          />
          <BaseAlert
            v-else-if="showLastAppointmentAt"
            icon="calendar"
            size="s"
            :tooltip="lastAppointmentTooltip"
            :text="`${$t('customer_details.last_appointment')} ${lastAppointmentFromNow}`"
          />
          <BaseAlert
            v-if="showAlertNotes && customer.alertNotes"
            color="error"
            size="s"
            :text="customer.alertNotes"
            v-test="'customer-alert-notification'"
          />
        </Flex>
      </Flex>
    </div>
  </BaseCard>
</template>

<script
  setup
  lang="ts"
>
import { useQuery } from '@vue/apollo-composable';
import { computed, ref, watch } from 'vue';
import dayjs from '@/dayjs';
import filters from '@/filters';
import SendMessage from '@/modules/customers/SendMessage.vue';
import { device } from '@/user-context';
import type { Customer, Option } from '../types';
import { GET_CUSTOMER_DETAILS } from './graphql';

const props = withDefaults(
  defineProps<{
    appointmentStartAt?: string;
    customer: Customer;
    loading?: boolean;
    options?: Option[] | string[];
    showAlertNotes?: boolean;
    showAddress?: boolean;
    showMedicalData?: boolean;
    error?: boolean;
    hasActions?: boolean;
  }>(),
  {
    hasActions: true,
  },
);

const emit = defineEmits(['select']);

const hasFullAddress = computed(() => {
  return (
    props.customer.address && props.customer.zipcode && props.customer.city
  );
});

const hasIncompleteAddress = computed(() => {
  return (
    !props.customer.address || !props.customer.zipcode || !props.customer.city
  );
});

const name = computed(
  () =>
    props.customer.fullName ||
    props.customer.name ||
    `${props.customer.firstName} ${props.customer.lastName}`,
);

const phoneNumber = computed(
  () => props.customer.mobilePhone || props.customer.phone,
);

const lastAppointmentAt = ref<string | null>(null);
const totalDue = ref<number | null>(null);
const hasCustomerDetailsResult = ref(false);

const { onResult } = useQuery(
  GET_CUSTOMER_DETAILS,
  () => ({
    id: props.customer.id,
    date: props.appointmentStartAt,
  }),
  {
    enabled: computed(() => !!props.customer.id && !!props.appointmentStartAt),
  },
);

onResult(({ data }) => {
  if (!data?.customer) {
    hasCustomerDetailsResult.value = false;
    lastAppointmentAt.value = null;
    totalDue.value = null;

    return;
  }

  hasCustomerDetailsResult.value = true;
  lastAppointmentAt.value = data.customer.lastAppointmentAt || null;
  totalDue.value = data.customer.totalDue || null;
});

const showNewCustomerAlert = computed(
  () => hasCustomerDetailsResult.value && !lastAppointmentAt.value,
);

const showLastAppointmentAt = computed(
  () =>
    lastAppointmentAt.value &&
    !dayjs(lastAppointmentAt.value).isSameOrAfter(
      dayjs(props.appointmentStartAt),
      'day',
    ),
);

const lastAppointmentTooltip = computed(() =>
  lastAppointmentAt.value ? filters.dateTime(lastAppointmentAt.value) : '',
);

const lastAppointmentFromNow = computed(() =>
  lastAppointmentAt.value ? filters.fromNow(lastAppointmentAt.value) : '',
);

const hasBottomAlerts = computed(
  () =>
    !!totalDue.value ||
    showNewCustomerAlert.value ||
    !!showLastAppointmentAt.value ||
    !!(props.showAlertNotes && props.customer.alertNotes),
);

watch(
  () => [props.customer.id, props.appointmentStartAt],
  () => {
    hasCustomerDetailsResult.value = false;
    lastAppointmentAt.value = null;
    totalDue.value = null;
  },
);

const hasViewCustomer = computed(() =>
  props.options?.find((option) => option === 'view' || option?.name === 'view'),
);
const actions = computed(
  () =>
    props.options?.filter(
      (option) => option !== 'view' && option?.name !== 'view',
    ) || [],
);

const showMessageModal = ref(false);

const onOptionSelect = (option: string) => {
  emit('select', option === 'clear' ? 'remove' : option);
};
</script>

<style
  lang="scss"
  module
>
.base {
  position: relative;
}

.content {
  .base.loading & {
    opacity: 0;
  }
}

.bottom {
  border-top: 1px solid var(--color-border);
  padding-top: $spacing;
}

.buttons {
  &:empty {
    display: none;
  }
}
</style>
